/**
 * Sample popup
 *
 * Opens a Drip Form rendered as a popup (Drip Form section with "Display as popup")
 * when a trigger button is clicked. Trigger buttons are rendered by the Sample
 * product grid section on each product card:
 *
 *   <button data-sample-popup-trigger
 *           data-sample-title="Neofollics Lotion"
 *           data-sample-popup-target="optional-popup-id">…</button>
 *
 * On open, the clicked card's product title is fuzzy-matched against the options of
 * the popup's "Type sample" dropdown ([data-drip-sample-select]) and the best match
 * is pre-selected. Matching is title-based, so no extra product metafield is needed.
 */

(() => {
  if (window.__samplePopupInit) return;
  window.__samplePopupInit = true;

  const STOP_WORDS = {
    de: 1, het: 1, een: 1, en: 1, the: 1, and: 1, of: 1, voor: 1, met: 1,
    ml: 1, gratis: 1, sample: 1, samples: 1,
  };

  /** Normalize a string: lowercase, strip accents and punctuation. */
  const normalize = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  /** Significant tokens of a string (no stop words, no pure numbers). */
  const tokenize = (value) =>
    normalize(value)
      .split(' ')
      .filter((token) => token && !STOP_WORDS[token] && !/^\d+$/.test(token));

  /**
   * Build, for each option, the set of "discriminating" tokens: its significant
   * tokens minus any token shared by EVERY option (e.g. a brand name like
   * "Neofollics"), which carries no signal for telling options apart. Without
   * this, a card sharing only the brand token would score 0.5 against a two-word
   * option and wrongly pre-select it. Falls back to the full token set for an
   * option that has only shared tokens.
   */
  const buildOptionTokenSets = (optionTexts) => {
    const perOption = optionTexts.map((text) => tokenize(text));
    const counts = {};
    perOption.forEach((tokens) => {
      new Set(tokens).forEach((token) => {
        counts[token] = (counts[token] || 0) + 1;
      });
    });
    const optionCount = perOption.filter((tokens) => tokens.length).length;
    return perOption.map((tokens) => {
      if (!tokens.length) return [];
      const discriminating = tokens.filter((token) => counts[token] < optionCount);
      return discriminating.length ? discriminating : tokens;
    });
  };

  /**
   * Score how well an option matches a product title.
   * Returns the fraction of the option's discriminating tokens found in the title.
   */
  const scoreOption = (titleTokenSet, optionTokens) => {
    if (!optionTokens.length) return 0;
    let hits = 0;
    optionTokens.forEach((token) => {
      if (titleTokenSet.has(token)) hits += 1;
    });
    return hits / optionTokens.length;
  };

  /** Commit a selection and notify listeners. */
  const applySelection = (select, index) => {
    select.selectedIndex = index;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Select the option whose value or visible text matches `value` exactly
   * (after normalization). Returns true if a match was applied.
   */
  const selectExact = (select, value) => {
    const wanted = normalize(value);
    if (!wanted) return false;
    const options = select.options;
    for (let i = 0; i < options.length; i += 1) {
      const option = options[i];
      if (!option.value || option.disabled) continue;
      if (normalize(option.value) === wanted || normalize(option.textContent) === wanted) {
        applySelection(select, i);
        return true;
      }
    }
    return false;
  };

  /**
   * Pre-select the sample in the popup's dropdown.
   * Prefers an exact match on the card's configured value; falls back to a
   * fuzzy match on the card heading so a card with only a heading still works.
   */
  const preselectSample = (popup, value, title) => {
    const select = popup.querySelector('[data-drip-sample-select]');
    if (!select) return;

    if (value && selectExact(select, value)) return;
    if (!title) return;

    const titleTokenSet = new Set(tokenize(title));
    if (!titleTokenSet.size) return;

    const options = Array.prototype.slice.call(select.options);
    const optionTokenSets = buildOptionTokenSets(
      options.map((option) => option.textContent || option.value)
    );

    let bestIndex = -1;
    let bestScore = 0;
    let bestTokenCount = 0;

    options.forEach((option, index) => {
      // Skip the disabled placeholder / empty option.
      if (!option.value || option.disabled) return;
      const optionTokens = optionTokenSets[index];
      const score = scoreOption(titleTokenSet, optionTokens);
      // Prefer a higher match fraction; break ties toward the more specific option.
      if (score > bestScore || (score === bestScore && optionTokens.length > bestTokenCount)) {
        bestScore = score;
        bestIndex = index;
        bestTokenCount = optionTokens.length;
      }
    });

    // Require at least half of an option's discriminating tokens to match.
    if (bestIndex !== -1 && bestScore >= 0.5) {
      applySelection(select, bestIndex);
    }
  };

  /** Find the popup that a trigger should open. */
  const findPopup = (trigger) => {
    const popups = document.querySelectorAll('[data-drip-popup]');
    if (!popups.length) return null;
    const targetId = trigger.getAttribute('data-sample-popup-target');
    if (targetId) {
      for (const popup of popups) {
        if (popup.getAttribute('data-drip-popup-id') === targetId) return popup;
      }
    }
    return popups[0];
  };

  let lastTrigger = null;

  /**
   * Move the popup to <body> so it escapes any section-level stacking context
   * (page sections animate via transform, which traps position:fixed and caps
   * its z-index below the sticky header/footer). Removes any stale copy left by
   * a theme-editor re-render of the same popup.
   */
  const portalToBody = (popup) => {
    if (popup.parentNode === document.body) return;
    const id = popup.getAttribute('data-drip-popup-id') || '';
    document.querySelectorAll('[data-drip-popup][data-portaled]').forEach((node) => {
      if (node !== popup && (node.getAttribute('data-drip-popup-id') || '') === id) {
        node.remove();
      }
    });
    popup.setAttribute('data-portaled', '');
    document.body.appendChild(popup);
  };

  const resetPopupSuccess = (popup) => {
    const form = popup.querySelector('form.drip-ef');
    const success = popup.querySelector('[data-drip-popup-success]');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
  };

  const openPopup = (popup, value, title, trigger) => {
    resetPopupSuccess(popup);
    preselectSample(popup, value, title);
    portalToBody(popup);
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sample-popup-open');
    lastTrigger = trigger || null;

    const focusTarget =
      popup.querySelector('input:not([type="hidden"]):not([tabindex="-1"]), select, textarea') ||
      popup.querySelector('.drip-form__close');
    if (focusTarget) focusTarget.focus();
  };

  const closePopup = (popup) => {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sample-popup-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
      lastTrigger = null;
    }
  };

  const closeAll = () => {
    document
      .querySelectorAll('[data-drip-popup].is-open')
      .forEach((popup) => closePopup(popup));
  };

  /**
   * Bind close handlers directly to each popup's close elements. The theme
   * editor intercepts/stops bubbling clicks for section selection, so a
   * document-delegated close handler never fires there — direct element
   * listeners are needed so the popup can always be closed.
   */
  const bindPopup = (popup) => {
    if (popup.getAttribute('data-close-bound') === 'true') return;
    popup.setAttribute('data-close-bound', 'true');
    popup.querySelectorAll('[data-drip-popup-close]').forEach((closer) => {
      closer.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closePopup(popup);
      });
    });
  };

  const bindAll = (root) => {
    (root || document).querySelectorAll('[data-drip-popup]').forEach(bindPopup);
  };

  // Open on trigger click (delegated, so dynamically rendered cards work too).
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-sample-popup-trigger]');
    if (!trigger) return;
    const popup = findPopup(trigger);
    if (!popup) return;
    event.preventDefault();
    openPopup(
      popup,
      trigger.getAttribute('data-sample-value'),
      trigger.getAttribute('data-sample-title'),
      trigger
    );
  });

  // Close on backdrop / close button (fallback for normal storefront bubbling).
  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-drip-popup-close]');
    if (!closer) return;
    const popup = closer.closest('[data-drip-popup]');
    if (popup) closePopup(popup);
  });

  // Close on Escape.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  // The Drip form submits natively: Drip's global account script (or a plain
  // form POST if that script is blocked) records the submission and redirects
  // to the configured thank-you page. We intentionally do NOT intercept submit
  // here — a fetch-based submit silently fails for ad-blocker users.

  // Initial bind + re-bind when a section (re)loads in the theme editor.
  bindAll(document);
  document.addEventListener('shopify:section:load', (event) => bindAll(event.target));
})();
